import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { MyDayDto, TaskDto } from '@lawyer/shared'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { useAuth } from '../auth/AuthProvider'
import { api } from '../lib/api'
import { formatDate, priorityColor, priorityLabel, statusColor, statusLabel } from '../lib/format'
import { qk } from '../lib/queryClient'

function TaskRow({ task, onOpen }: { task: TaskDto; onOpen: () => void }) {
  const context = [task.caseName, task.clientName, task.projectName].filter(Boolean).join(' · ')

  return (
    <Box
      onClick={onOpen}
      sx={{
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        transition: 'background-color 120ms',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500, minWidth: 0 }} noWrap>
          {task.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {formatDate(task.dueDate)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
        <Chip size="small" label={statusLabel(task.status)} color={statusColor(task.status)} variant="outlined" />
        <Chip size="small" label={priorityLabel(task.priority)} color={priorityColor(task.priority)} />
        {context ? (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {context}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  )
}

function Bucket({
  title,
  tasks,
  emptyText,
  accent,
  onOpen,
}: {
  title: string
  tasks: TaskDto[]
  emptyText: string
  accent: string
  onOpen: (task: TaskDto) => void
}) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Chip size="small" label={tasks.length} variant="outlined" />
      </Stack>

      {tasks.length === 0 ? (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ px: 2, py: 4, textAlign: 'center', flexGrow: 1 }}
        >
          {emptyText}
        </Typography>
      ) : (
        <Stack divider={<Divider />}>
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} onOpen={() => onOpen(task)} />
          ))}
        </Stack>
      )}
    </Paper>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.myDay,
    queryFn: () => api.get<MyDayDto>('/dashboard/my-day'),
  })

  const openTask = (task: TaskDto) => navigate(`/tasks?taskId=${task.id}`)
  const total = data ? data.overdue.length + data.today.length + data.thisWeek.length : 0

  return (
    <>
      <PageHeader
        title={`שלום, ${user?.name ?? ''}`}
        subtitle={
          data
            ? total === 0
              ? 'אין משימות פתוחות עם תאריך יעד קרוב'
              : `${total} משימות משויכות אליך לשבוע הקרוב`
            : 'המשימות שמשויכות אליך, לפי תאריך היעד'
        }
        actions={
          <Button variant="outlined" endIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')}>
            כל המשימות
          </Button>
        }
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {/* The three buckets always render. Collapsing them into one empty state left a
          blank landing page for anyone with nothing due. */}
      {data ? (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
          <Bucket
            title="באיחור"
            accent="error.main"
            tasks={data.overdue}
            emptyText="אין משימות באיחור"
            onOpen={openTask}
          />
          <Bucket
            title="להיום"
            accent="warning.main"
            tasks={data.today}
            emptyText="אין משימות להיום"
            onOpen={openTask}
          />
          <Bucket
            title="השבוע"
            accent="primary.main"
            tasks={data.thisWeek}
            emptyText="אין משימות לשבוע הקרוב"
            onOpen={openTask}
          />
        </Stack>
      ) : null}
    </>
  )
}
